export const algoliaClient = (appId, apiKey) => algoliasearch(appId, apiKey);

export const waitForAlgoliaClient = (count = 0) =>
  new Promise((resolve, reject) => {
    const isAlgoliaInitialized = window.algoliaShopify?.config;

    setTimeout(() => {
      if (!isAlgoliaInitialized && count < 100) {
        resolve(waitForAlgoliaClient(count + 1));
      } else if (isAlgoliaInitialized) {
        resolve();
      } else {
        reject("Algolia has not been initialized!");
      }
    }, 100);
  });

export const responsiveImage = (alt, image, addSettings) => {
  const settings = {
    alt: alt,
    image: image,
    imageSize: "304x395",
    imageBreakpoints: ["360", "480", "768", "1024", "1200", "1440"],
    imageSizes: [
      "150x195",
      "210x273",
      "354x460",
      "288x374",
      "249x324",
      "304x395",
    ],
    ...addSettings,
  };

  // Return fallback image if no image found
  if (!settings.image) {
    return `
      <picture>
        <img loading="lazy" alt="${settings.alt}" width="304" height="395" src="http://cdn.shopify.com/s/images/admin/no-image-compact.gif">
      </picture>
    `;
  }

  // Build image
  const imageHtml = `
    <img
      loading="lazy"
      alt="${settings.alt}"
      width="${settings.imageSize.split("x")[0]}"
      height="${settings.imageSize.split("x")[1]}"
      src="${settings.image.replace(
        /\/(.*)\.(\w{2,4})/g,
        "/$1_" + settings.imageSize + "_crop_center.$2"
      )}"
      srcset="${settings.image.replace(
        /\/(.*)\.(\w{2,4})/g,
        "/$1_" + settings.imageSize + "_crop_center.$2"
      )} 1x, ${settings.image.replace(
    /\/(.*)\.(\w{2,4})/g,
    "/$1_" + settings.imageSize + "_crop_center@2x.$2"
  )} 2x"
    >
  `;

  // Build sources
  const sources = settings.imageSizes
    .map((size, index) => {
      return `
      <source
        media="(max-width: ${settings.imageBreakpoints[index]}px)"
        srcset="${settings.image.replace(
          /\/(.*)\.(\w{2,4})/g,
          "/$1_" + size + "_crop_center.$2"
        )} 1x,
        ${settings.image.replace(
          /\/(.*)\.(\w{2,4})/g,
          "/$1_" + size + "_crop_center@2x.$2"
        )} 2x"
      >
    `;
    })
    .join("");

  return `
    <picture>
      ${sources}
      ${imageHtml}
    </picture>
  `;
};

export const parseColorsFromAlgoliaNamedTags = (algoliaItem, colorsMap) => {
  let currentColor = algoliaItem?.named_tags["product-colour"];
  const groupColors = algoliaItem?.named_tags["colour-group-product"];

  if (!currentColor) {
    return [];
  }

  if (Array.isArray(currentColor)) {
    currentColor = currentColor[0];
  }

  const currentColorHandleized = currentColor
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-$/, "")
    .replace(/^-/, "");

  const allColors = [
    {
      handle: algoliaItem.handle,
      colorLabel: currentColor,
      colorHex: colorsMap[currentColorHandleized] || "",
    },
  ];

  if (!groupColors) {
    return allColors;
  }

  const groupColorArray = Array.isArray(groupColors)
    ? groupColors
    : [groupColors];

  groupColorArray.forEach((colorStr) => {
    const parts = colorStr.replace(/\s+/g, "").split("|");
    if (parts[0] && parts[1]) {
      allColors.push({
        handle: parts[0],
        colorLabel: parts[1],
        colorHex: colorsMap[parts[1]] || "",
      });
    }
  });

  return allColors;
};

export const formatMoney = (cents) => {
  let priceInCents = cents;

  /**
   * Uncomment the following block to support Shopify multi currency
   * and convert prices from store currency to the currency selected by
   * the user (aka "presentment" currency).
   * Please note that this relies on the `Shopify.currency` object exposed
   * by Shopify which is an undocumented feature and can change at any time.
   */
  if (
    typeof Shopify !== "undefined" &&
    typeof Shopify.currency !== "undefined" &&
    typeof Shopify.currency.rate !== "undefined" &&
    typeof Shopify.currency.active !== "undefined"
  ) {
    // Convert to "presentment" currency using the rate provided by Shopify
    priceInCents = priceInCents * Shopify.currency.rate;
    // Round up to the nearest whole number keeping in accordance with
    // the default rounding strategy of Shopify
    priceInCents = Math.ceil(priceInCents);

    /**
     * Shopify uses different rounding strategies for different currencies.
     * The logic below might need to be customised based on the active currencies.
     * Note: Please only include currencies other than your store currency.
     */
    var storeCurrency = document.body.dataset.storeCurrency;

    if (Shopify.currency.active !== storeCurrency) {
      // Round up to the 95th cent for EUR
      if (Shopify.currency.active === "EUR") {
        //priceInCents = parseInt(priceInCents / 100, 10) * 100 + 95;

        // Actually round up to the 00th cent
        priceInCents = parseInt(priceInCents / 100, 10) * 100 + 100;
      }
      // Round up to the 00th cent for GBP, USD, DKK
      else if (
        Shopify.currency.active === "AUD" ||
        Shopify.currency.active === "CAD" ||
        Shopify.currency.active === "HKD" ||
        Shopify.currency.active === "JPY" ||
        Shopify.currency.active === "NZD" ||
        Shopify.currency.active === "SAR" ||
        Shopify.currency.active === "SGD" ||
        Shopify.currency.active === "SEK" ||
        Shopify.currency.active === "CHF" ||
        Shopify.currency.active === "AED" ||
        Shopify.currency.active === "USD"
      ) {
        priceInCents = parseInt(priceInCents / 100, 10) * 100 + 100;
      }
    }
  }

  var val = (priceInCents / 100.0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  var format = window.algolia.moneyFormat;

  // Not necessary, but allows for more risk tolerance if Shopify.formatMoney doesn't work as we want
  var regexp = /^([^{}]*)\{\{amount\}\}([^{}]*)$/;
  if (format.match(regexp)) {
    return format.replace(regexp, "$1" + val + "$2");
  }

  regexp = /^([^{}]*)\{\{amount_with_comma_separator\}\}([^{}]*)$/;
  if (format.match(regexp)) {
    var money = val.replace(/[.]/, "|");
    money = money.replace(/[,]/, ".");
    money = money.replace(/[|]/, ",");
    return format.replace(regexp, "$1" + money + "$2");
  }

  if (window.Shopify && window.Shopify.formatMoney) {
    return window.Shopify.formatMoney(priceInCents, format);
  }

  return "$" + val;
};

export const formatPrice = (value) => formatMoney(Number(value) * 100);

export const formatPriceWithComparison = (price, compareAtPrice) => {
  const comparing =
    Number(compareAtPrice) && Number(compareAtPrice) > Number(price);
  let res = `
    <span class="${comparing ? "text-red" : "text-primary"}">
      ${formatPrice(price)}
    </span>
  `;

  if (comparing) {
    res += ` <span class="text-gray"><s>${formatPrice(
      compareAtPrice
    )}</s></span>`;
  }

  return res;
};

export const hasFacetValues = (resultState, facetName) => {
  const isFacetValueAvailable = resultState.facets.some(
    (facet) => facet.name === facetName
  );
  const isDisjunctiveFacetValueAvailable = resultState.disjunctiveFacets.some(
    (facet) => facet.name === facetName
  );
  const isHierarchicalFacetValueAvailable = resultState.hierarchicalFacets.some(
    (facet) => facet.name === facetName
  );

  return (
    isFacetValueAvailable ||
    isDisjunctiveFacetValueAvailable ||
    isHierarchicalFacetValueAvailable
  );
};

export const saveForConversionTracking = (data) => {
  /**
   * We're using a try, catch here to handle any possible exceptions
   * resulting from local storage or JSON parsing.
   */
  try {
    // Get any data previously stored
    const previousClickItemsString =
      localStorage.getItem("algolia_analytics_clicked_objects") || "[]";
    const previousClickItems = JSON.parse(previousClickItemsString);

    var conversionData = data;

    // Changing the event to 'convert' from 'click'
    conversionData.eventName = "convert";
    // Removing the `positions` property
    delete conversionData.positions;

    // Add the current products data to local storage
    previousClickItems.push(conversionData);
    localStorage.setItem(
      "algolia_analytics_clicked_objects",
      JSON.stringify(previousClickItems)
    );
  } catch (error) {
    // No need to do anything in this scenario
  }
};

const getTrackedUiState = (uiState) => {
  const trackedUiState = {};
  Object.keys(uiState).forEach(function (k) {
    if (k === "configure" || k === "query" || k === "q") {
      return;
    }
    trackedUiState[k] = uiState[k];
  });
  return trackedUiState;
};

export const indexRouting = (indexName, allowedParams) => {
  /**
   * List of URL params which are "allowed" by InstantSearch.
   * These parameters won't be overwritten when the search state is written
   * to the URL.
   */
  const foreignParamsArray = allowedParams
    .replace(/\s+/g, "")
    .split(",")
    .filter((param) => param);

  const ALLOWED_FOREIGN_PARAMS = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "utm_content",
    ...foreignParamsArray,
  ];

  let foreignRouteState;

  return {
    /**
     * `stateToRoute` method transforms the `uiState` object from InstantSearch
     * into an object that will then be serialized to construct the URL.
     */
    stateToRoute: (uiState) => {
      const route = getTrackedUiState(uiState[indexName] || {});
      route.q = uiState[indexName].query;
      return Object.assign({}, foreignRouteState, route);
    },

    /**
     * `routeToState` method transforms the route object into a `uiState` object
     * so that InstantSearch is initialized with the correct state from the
     * URL.
     */
    routeToState: (routeState) => {
      /**
       * Backup parameters from `routeState` into a different object
       * `foreignRouteState` so that we can re-inject them into the object
       * returned within `stateToRoute`.
       * This allows us to preserve the parameters specified in `ALLOWED_FOREIGN_PARAMS`
       * array.
       */
      if (!foreignRouteState) {
        foreignRouteState = ALLOWED_FOREIGN_PARAMS.reduce(function (acc, key) {
          acc[key] = routeState[key];
          return acc;
        }, {});
      }

      const state = {};
      state[indexName] = getTrackedUiState(routeState || {});
      state[indexName].query = routeState.q;
      return state;
    },
  };
};
